import { useCallback, useRef } from 'react'
import { Col, Row } from 'react-grid-system'
import { NormalFormDateField, NormalFormSelectionField, NormalFormTextField } from '../molecules'
import { useUserLanguage } from '@/customHooks'
import { CreateExpenseInput, CurrencyOption, IncidentCategoryOption } from '@/graphql/generated'
import textsData from '@/textData/ExpenseFormTexts.json'
import { incidentCategoryMapper } from '@/helpers'

type AddExpenseFormControlledProps = {
  onChange: (data: CreateExpenseInput) => void
  defaultData?: CreateExpenseInput
}

const emptyData: CreateExpenseInput = {
  title: '',
  category: IncidentCategoryOption.Other,
  currency: CurrencyOption.Usd,
  // La API espera un string YYYY-MM-DD; inicializamos vacío
  date: '',
  invoiceNumber: '',
  expenseCost: '',
  description: '',
}

export const AddExpenseFormControlled = ({ onChange, defaultData }: AddExpenseFormControlledProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.form : textsData.en.form

  const expenseDataRef = useRef<CreateExpenseInput>(defaultData || emptyData)

  const categoryOptions = Object.values(IncidentCategoryOption).map((value) => ({
    name: incidentCategoryMapper(value, userLanguage),
    value,
  }))

  const onChangeHandle = useCallback(
    (field: keyof CreateExpenseInput, value: string | Date | null) => {
      let processedValue: string | null | Date = value

      if (field === 'date') {
        if (value instanceof Date && !isNaN(value.getTime())) {
          // Normalizamos a YYYY-MM-DD solo si la fecha es válida
          processedValue = value.toISOString().slice(0, 10)
        } else if (typeof value === 'string' && value.trim() !== '') {
          // Validar que el string tenga el formato correcto YYYY-MM-DD
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/
          if (dateRegex.test(value)) {
            processedValue = value.slice(0, 10)
          } else {
            // Si no tiene el formato correcto, intentar parsearlo
            const parsedDate = new Date(value)
            if (!isNaN(parsedDate.getTime())) {
              processedValue = parsedDate.toISOString().slice(0, 10)
            } else {
              processedValue = ''
            }
          }
        } else {
          // Si es null, undefined o string vacío, mantener como string vacío
          processedValue = ''
        }
      }

      expenseDataRef.current = {
        ...expenseDataRef.current,
        [field]: processedValue as any,
      }
      onChange(expenseDataRef.current)
    },
    [onChange],
  )

  return (
    <>
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.title.label}
            placeholder={texts.title.placeholder}
            name="expenseTitle"
            onChange={(value) => onChangeHandle('title', value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormSelectionField<IncidentCategoryOption>
            label={texts.category.label}
            name="expenseCategory"
            options={categoryOptions}
            onChange={(value) => onChangeHandle('category', value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormDateField
            label={texts.date.label}
            name="expenseDate"
            onChange={(value) => onChangeHandle('date', value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.invoiceNumber.label}
            placeholder={texts.invoiceNumber.placeholder}
            name="invoiceNumber"
            onChange={(value) => onChangeHandle('invoiceNumber', value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.cost.label}
            placeholder={texts.cost.placeholder}
            name="expenseCost"
            onChange={(value) => onChangeHandle('expenseCost', value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.description.label}
            placeholder={texts.description.placeholder}
            name="expenseDescription"
            onChange={(value) => onChangeHandle('description', value)}
            required
          />
        </Col>
      </Row>
    </>
  )
}


